/* @flow */
import { ApolloLink, Observable, Observer, Operation, NextLink } from 'apollo-link';

const MAX_RETRIES = 3;
const RETRY_INTERVAL = 500; // ms

type Options = {
  shouldRetry: (Operation, *[]) => boolean,
};

class RetryableOperation {
  operation: Operation;
  forward: NextLink;
  retryCount: number;
  observer: ?Observer;
  currentSubscription: ?Observable;
  timerId: ?TimeoutID;
  shouldRetry: (Operation, *[]) => boolean;
  complete: boolean;

  constructor(operation: Operation, forward: NextLink, shouldRetry: (Operation, *[]) => boolean) {
    this.operation = operation;
    this.forward = forward;
    this.retryCount = 0;
    this.observer = null;
    this.currentSubscription = null;
    this.timerId = null;
    this.shouldRetry = shouldRetry;
    this.complete = false;
  }

  subscribe(observer: Observer) {
    this.observer = observer;
  }

  unsubscribe() {
    this.observer = null;
    if (this.currentSubscription) {
      this.currentSubscription.unsubscribe();
      this.currentSubscription = null;
    }
    if (this.timerId) {
      clearTimeout(this.timerId);
    }
    this.timerId = null;
  }

  try() {
    this.currentSubscription = this.forward(this.operation).subscribe({
      next: this.onNext,
      error: this.onError,
      complete: this.onComplete,
    });
  }

  onNext = (value: *) => {
    const { errors } = value;
    if (errors) {
      if (this.retryCount < MAX_RETRIES && this.shouldRetry(this.operation, errors)) {
        this.scheduleRetry();
        this.retryCount += 1;
        return;
      }
    }
    this.complete = true;
    if (this.observer) {
      this.observer.next(value);
    }
  };

  onError = (error: *) => {
    if (this.retryCount < MAX_RETRIES && this.shouldRetry(this.operation, [error])) {
      this.scheduleRetry();
      this.retryCount += 1;
      return;
    }
    if (this.observer) {
      this.observer.error(error);
    }
  };

  onComplete = () => {
    if (this.complete && this.observer) {
      this.observer.complete();
    }
  };

  scheduleRetry() {
    this.timerId = setTimeout(() => {
      this.timerId = null;
      this.try();
    }, RETRY_INTERVAL);
  }
}

/* The retry link is responsible for retrying any
 * operations allowed by the shouldRetry predicate
 * function that failed because of a query or network error.
 *
 * const retryLink = RetryLink({
 *    shouldRetry: (operation, errors) => {
 *      const { operationName } = operation;
 *      if (operationName === 'ContentSourcesContent') {
 *        return errors.map(error => get(error, 'serviceMetadata.service_status')).indexOf(503) >= 0
 *      }
 *      return false;
 *    }
 * });
 *
 * Based off of:
 * https://github.com/apollographql/apollo-link/tree/master/packages/apollo-link-retry
 */
export class RetryLink extends ApolloLink {
  constructor(options: Options) {
    super();
    this.options = options;
  }

  request(operation: *, forward: *) {
    const { shouldRetry } = this.options;
    const retryable = new RetryableOperation(operation, forward, shouldRetry);
    return new Observable(observer => {
      retryable.subscribe(observer);
      retryable.try();
      return () => {
        retryable.unsubscribe();
      };
    });
  }
}
